"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Alert } from "reactstrap"
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs"
import ExhibitionCountdownWidget from "./ExhibitionCountdownWidget"
import StaffDashboardStats from "./StaffDashboardStats"
import DashboardService from "../../../Service/staffDashboardService"
import type { StaffDashboardData } from "./staff-dashboard"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

const StaffDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<StaffDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const data = await DashboardService.getStaffDashboardData()
      setDashboardData(data)
    } catch (err) {
      console.error("Failed to fetch staff dashboard data:", err)
      setError(err instanceof Error ? err.message : "Failed to load dashboard data")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboardData()

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000)

    return () => clearInterval(interval)
  }, [fetchDashboardData])

  return (
    <div className="db-page">
      <Breadcrumbs mainTitle="Dashboard" parent="Home" />

      <Container fluid className="pb-4">
        {/* Error Alert */}
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert color="warning" className="d-flex align-items-center">
                <i className="fa fa-exclamation-triangle me-2"></i>
                <div>
                  <strong>Warning:</strong> {error}
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Exhibition Countdown Widget */}
        <div className="db-section">
          <ExhibitionCountdownWidget />
        </div>

        {/* Dashboard Stats */}
        <div className="db-section mb-0">
          <div className="db-section-head">
            <span className="db-section-bar" style={{ background: "var(--db-indigo)" }}></span>
            <div>
              <p className="db-section-eyebrow">Overview</p>
              <h3 className="db-section-title">Key Metrics</h3>
            </div>
          </div>
          <StaffDashboardStats data={dashboardData} loading={loading} />
        </div>
      </Container>
    </div>
  )
}

export default StaffDashboard
